import React, { JSX, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  PresenceEntry,
  PresenceItem,
  PresenceStep,
  SCENE_FADE_MS,
  SceneTransition,
  TimerRequest,
  applyTake,
  contentReady,
  holdExpired,
  itemsOf,
  pruneVanished,
  settle,
  timerFor
} from 'lib/scenePresence'
import { SceneElement } from 'types/api'

export interface ScenePresenceOptions {
  takeId: string | null
  elements: SceneElement[]
  transition: SceneTransition
  fadeMs?: number
  skipWhenHidden?: boolean
}

export interface ScenePresence {
  items: PresenceItem[]
  onContentReady: (key: string) => void
}

type Timer = ReturnType<typeof globalThis.setTimeout>

interface PresenceEngine {
  entries: PresenceEntry[]
  takeId: string | null | undefined
  fadeMs: number
  timers: Map<string, Timer>
  run: (reduce: (entries: PresenceEntry[], now: number) => PresenceStep) => void
  schedule: (request: TimerRequest) => void
  clearAll: () => void
}

const createPresenceEngine = (render: (entries: PresenceEntry[]) => void, fadeMs: number): PresenceEngine => {
  const engine: PresenceEngine = {
    entries: [],
    takeId: undefined,
    fadeMs,
    timers: new Map(),
    run: (reduce) => {
      const step = reduce(engine.entries, globalThis.performance.now())
      if (step.entries !== engine.entries) {
        engine.entries = step.entries
        render(step.entries)
      }
      step.timers.forEach(engine.schedule)
    },
    schedule: ({ key, action, delayMs }) => {
      globalThis.clearTimeout(engine.timers.get(key))
      engine.timers.delete(key)
      if (action == null) return
      engine.timers.set(key, globalThis.setTimeout(() => {
        engine.timers.delete(key)
        engine.run((entries, now) => action === 'hold'
          ? holdExpired(entries, key, now, engine.fadeMs)
          : settle(entries, key, now))
      }, delayMs))
    },
    clearAll: () => {
      engine.timers.forEach((timer) => globalThis.clearTimeout(timer))
      engine.timers.clear()
    }
  }
  return engine
}

export const useScenePresence = ({
  takeId,
  elements,
  transition,
  fadeMs = SCENE_FADE_MS,
  skipWhenHidden = false
}: ScenePresenceOptions): ScenePresence => {
  const [entries, setEntries] = useState<PresenceEntry[]>([])
  const engineRef = useRef<PresenceEngine | null>(null)
  if (engineRef.current == null) engineRef.current = createPresenceEngine(setEntries, fadeMs)
  const engine = engineRef.current
  engine.fadeMs = fadeMs

  useLayoutEffect(() => {
    if (engine.takeId === takeId) return
    engine.takeId = takeId
    const hidden = skipWhenHidden && globalThis.document.hidden
    const effective: SceneTransition = hidden ? 'CUT' : transition
    engine.run((current, now) => applyTake(current, elements, effective, now, engine.fadeMs))
  }, [takeId])

  useLayoutEffect(() => {
    if (engine.takeId !== takeId) return
    engine.run((current) => pruneVanished(current, elements))
  }, [elements])

  useEffect(() => {
    engine.entries.forEach((e) => {
      if (engine.timers.has(e.key)) return
      const request = timerFor(e, engine.fadeMs)
      if (request != null) engine.schedule(request)
    })
    return () => engine.clearAll()
  }, [engine])

  const onContentReady = useCallback((key: string): void => {
    engine.run((current, now) => contentReady(current, key, now, engine.fadeMs))
  }, [engine])

  const items = useMemo(() => itemsOf(entries, fadeMs), [entries, fadeMs])
  return { items, onContentReady }
}

export const PresenceLayer = ({ item, children }: { item: PresenceItem, children: ReactNode }): JSX.Element => {
  const [armed, setArmed] = useState(!item.fadeIn)
  useLayoutEffect(() => {
    if (armed) return
    const raf = globalThis.requestAnimationFrame(() => setArmed(true))
    return () => globalThis.cancelAnimationFrame(raf)
  }, [armed])
  return (
    <div
      data-presence-key={item.key}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: item.el.z,
        opacity: armed ? item.opacity : 0,
        transition: item.opacityMs > 0 ? `opacity ${item.opacityMs}ms ease-in-out` : 'none',
        pointerEvents: 'none'
      }}
    >
      {children}
    </div>
  )
}
